import React from 'react';
import {
  ProfitabilityMetrics,
  EfficiencyMetrics,
  getWinRateBenchmark,
  getWinLossRatioBenchmark,
  getProfitFactorBenchmark,
  getFeeRatioBenchmark,
  getBenchmarkIcon,
} from '../../utils/breakdownUtils';
import './ByPerformanceTab.css';

interface ByPerformanceTabProps {
  profitabilityMetrics: ProfitabilityMetrics;
  efficiencyMetrics: EfficiencyMetrics;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ByPerformanceTab: React.FC<ByPerformanceTabProps> = ({
  profitabilityMetrics,
  efficiencyMetrics,
  isLoading,
  error,
  onRetry,
}) => {
  const formatCurrency = (value: number) => {
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);
    return `${sign}$${absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatRatio = (value: number) => {
    return `${value.toFixed(2)}:1`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00Z');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getBenchmarkClass = (level: string) => {
    return `benchmark-${level}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="breakdown-loading">
        <div className="breakdown-loading-spinner" />
        <span>Calculating performance metrics...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="breakdown-error">
        <div className="breakdown-error-title">Failed to load performance metrics</div>
        <div className="breakdown-error-message">{error}</div>
        {onRetry && (
          <button className="breakdown-retry-button" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (profitabilityMetrics.total_trading_days === 0) {
    return (
      <div className="breakdown-empty">
        <div className="breakdown-empty-icon">📊</div>
        <div className="breakdown-empty-title">No performance data</div>
        <div className="breakdown-empty-message">
          No trading data available to calculate performance metrics.
        </div>
        <div className="breakdown-empty-suggestion">
          Select a date range with trading activity.
        </div>
      </div>
    );
  }

  const winRateBenchmark = getWinRateBenchmark(profitabilityMetrics.win_rate);
  const winLossRatioBenchmark = getWinLossRatioBenchmark(profitabilityMetrics.win_loss_ratio);
  const profitFactorBenchmark = getProfitFactorBenchmark(profitabilityMetrics.profit_factor);
  const feeRatioBenchmark = getFeeRatioBenchmark(efficiencyMetrics.fee_ratio);

  return (
    <div className="by-performance-tab">
      {/* Profitability Metrics Section */}
      <div className="performance-section">
        <h4 className="performance-section-title">Profitability Metrics</h4>
        
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Win Rate</div>
            <div className="metric-value">
              {formatPercent(profitabilityMetrics.win_rate)}
              <span className={`metric-benchmark ${getBenchmarkClass(winRateBenchmark)}`}>
                {getBenchmarkIcon(winRateBenchmark)}
              </span>
            </div>
            <div className="metric-detail">
              {profitabilityMetrics.total_profitable_days}/{profitabilityMetrics.total_trading_days} profitable days
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Average Win</div>
            <div className="metric-value profit-positive">
              {formatCurrency(profitabilityMetrics.avg_win)}
            </div>
            <div className="metric-detail">Per winning day</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Average Loss</div>
            <div className="metric-value profit-negative">
              {formatCurrency(profitabilityMetrics.avg_loss)}
            </div>
            <div className="metric-detail">Per losing day</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Win/Loss Ratio</div>
            <div className="metric-value">
              {formatRatio(profitabilityMetrics.win_loss_ratio)}
              <span className={`metric-benchmark ${getBenchmarkClass(winLossRatioBenchmark)}`}>
                {getBenchmarkIcon(winLossRatioBenchmark)}
              </span>
            </div>
            <div className="metric-detail">
              {winLossRatioBenchmark === 'excellent' ? 'Strong' : 
               winLossRatioBenchmark === 'good' ? 'Good' :
               winLossRatioBenchmark === 'warning' ? 'Fair' : 'Weak'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Profit Factor</div>
            <div className="metric-value">
              {profitabilityMetrics.profit_factor.toFixed(2)}
              <span className={`metric-benchmark ${getBenchmarkClass(profitFactorBenchmark)}`}>
                {getBenchmarkIcon(profitFactorBenchmark)}
              </span>
            </div>
            <div className="metric-detail">Gross profit / gross loss</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Best Day</div>
            <div className="metric-value profit-positive">
              {profitabilityMetrics.best_day ? formatCurrency(profitabilityMetrics.best_day.profit) : 'N/A'}
            </div>
            <div className="metric-detail">
              {profitabilityMetrics.best_day ? formatDate(profitabilityMetrics.best_day.date) : 'No profitable days'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Worst Day</div>
            <div className="metric-value profit-negative">
              {profitabilityMetrics.worst_day ? formatCurrency(profitabilityMetrics.worst_day.profit) : 'N/A'}
            </div>
            <div className="metric-detail">
              {profitabilityMetrics.worst_day ? formatDate(profitabilityMetrics.worst_day.date) : 'No losing days'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Max Drawdown</div>
            <div className="metric-value profit-negative">
              {profitabilityMetrics.max_drawdown > 0 ? formatCurrency(-profitabilityMetrics.max_drawdown) : '$0.00'}
            </div>
            <div className="metric-detail">Largest peak-to-trough decline</div>
          </div>
        </div>
      </div>

      {/* Efficiency Metrics Section */}
      <div className="performance-section">
        <h4 className="performance-section-title">Efficiency Metrics</h4>
        
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Avg Profit/Trade</div>
            <div className={`metric-value ${efficiencyMetrics.avg_profit_per_trade >= 0 ? 'profit-positive' : 'profit-negative'}`}>
              {formatCurrency(efficiencyMetrics.avg_profit_per_trade)}
            </div>
            <div className="metric-detail">Net profit per trade</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Fee Ratio</div>
            <div className="metric-value">
              {formatPercent(efficiencyMetrics.fee_ratio)}
              <span className={`metric-benchmark ${getBenchmarkClass(feeRatioBenchmark)}`}>
                {getBenchmarkIcon(feeRatioBenchmark)}
              </span>
            </div>
            <div className="metric-detail">
              {feeRatioBenchmark === 'excellent' ? 'Excellent' : 
               feeRatioBenchmark === 'good' ? 'Good' :
               feeRatioBenchmark === 'warning' ? 'Acceptable' : 'High'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Cost Efficiency</div>
            <div className="metric-value profit-positive">
              ${efficiencyMetrics.cost_efficiency.toFixed(2)}
            </div>
            <div className="metric-detail">Net profit per $1 of fees</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Trading Days</div>
            <div className="metric-value">
              {efficiencyMetrics.trading_days}
            </div>
            <div className="metric-detail">Days with at least one trade</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Trades/Day Avg</div>
            <div className="metric-value">
              {efficiencyMetrics.trades_per_day_avg.toFixed(1)}
            </div>
            <div className="metric-detail">Average trades per active day</div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Total Trades</div>
            <div className="metric-value">
              {efficiencyMetrics.total_trades.toLocaleString()}
            </div>
            <div className="metric-detail">Across all trading days</div>
          </div>
        </div>

        {/* Contextual Insights */}
        {feeRatioBenchmark === 'poor' && (
          <div className="performance-insights">
            <div className="insight-item">
              ⚠️ Fee ratio is elevated — consider optimizing trade size to reduce fee impact
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ByPerformanceTab;
